import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  onSnapshot,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../../firebase/config';

// Konstanten
const SETTINGS_COLLECTION = 'appSettings';
const SETTINGS_DOC_ID = 'general';
const CF_SETTINGS_DOC_ID = 'cloudfunctions';

// Default-Werte für die Einstellungen
const defaultSettings = {
  // Treuekarten-Einstellungen
  loyaltyCardActive: false,
  loyaltyCardRidesRequired: 5,
  loyaltyCardDiscountEur: 5,
  loyaltyCardStartDate: null,  // Wird beim Aktivieren automatisch gesetzt
  loyaltyCardEndDate: null,
  loyaltyCardNoEndDate: true,
  // Vorbestellungen
  showPreordersToAllDrivers: false,
  autoAcceptPreBookings: false,
  // Mobile App Store Links
  appStoreUrl: '',
  playStoreUrl: '',
  // Tarif-Einstellungen
  tarifGrundpreis: 3.5,
  tarifPreisProKm: 2.5,
  tarifGrossraumzuschlag: 5.0,
  // Nachttarif-Einstellungen
  nachtTarifAktiv: false,
  nachtTarifGrundpreis: 3.5,
  nachtTarifPreisProKm: 2.5,
  nachtTarifWocheStart: '22:00',
  nachtTarifWocheEnd: '06:00',
  nachtTarifWochenendStart: '20:00',
  nachtTarifWochenendEnd: '07:00',
  // Fahrpreis-Anzeige für Kunden
  showFareToCustomer: false,
  // Fahrzeuglimit (wird manuell in Firestore gesetzt, hier nur Fallback)
  vehicleLimit: 10,
  // Sofortbestellungen: Anzeige-Methode für Fahrer ('distance' | 'freimeldung')
  sofortbestellungAnzeigeMethode: 'distance'
};

// Default-Werte für Cloud Function Einstellungen
const defaultCloudFunctionSettings = {
  autoLogoutInactiveDriversEnabled: true,
  autoLogoutBackgroundMinutes: 120,
};

// Listener-Referenz für Cleanup
let settingsListener = null;

// Async Thunks

/**
 * Lädt die globalen Einstellungen aus Firebase
 */
export const fetchSettings = createAsyncThunk(
  'settings/fetchSettings',
  async (_, { rejectWithValue }) => {
    try {
      const settingsRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        return {
          ...defaultSettings,
          ...data,
          // Timestamps zu ISO-Strings konvertieren für Redux
          loyaltyCardStartDate: data.loyaltyCardStartDate
            ? data.loyaltyCardStartDate.toDate().toISOString().split('T')[0]
            : null,
          loyaltyCardEndDate: data.loyaltyCardEndDate
            ? data.loyaltyCardEndDate.toDate().toISOString().split('T')[0]
            : null,
          lastUpdated: data.lastUpdated
            ? data.lastUpdated.toDate().toISOString()
            : null
        };
      } else {
        // Dokument existiert nicht, erstelle es mit Default-Werten
        await setDoc(settingsRef, {
          ...defaultSettings,
          createdAt: serverTimestamp(),
          lastUpdated: serverTimestamp()
        });
        return defaultSettings;
      }
    } catch (error) {
      console.error('Fehler beim Laden der Einstellungen:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Speichert eine einzelne globale Einstellung sofort in Firebase
 */
export const saveSettingImmediately = createAsyncThunk(
  'settings/saveSettingImmediately',
  async ({ key, value }, { rejectWithValue }) => {
    try {
      const settingsRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
      const settingsDoc = await getDoc(settingsRef);

      const dataToSave = {
        [key]: value,
        lastUpdated: serverTimestamp()
      };

      if (settingsDoc.exists()) {
        await updateDoc(settingsRef, dataToSave);
      } else {
        await setDoc(settingsRef, {
          ...defaultSettings,
          ...dataToSave,
          createdAt: serverTimestamp()
        });
      }

      return { [key]: value, lastUpdated: new Date().toISOString() };
    } catch (error) {
      console.error('Fehler beim Speichern der Einstellung:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Speichert die globalen Einstellungen in Firebase
 */
export const saveSettings = createAsyncThunk(
  'settings/saveSettings',
  async (settings, { rejectWithValue, getState }) => {
    try {
      const settingsRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);

      // Hole aktuelle Einstellungen aus dem State
      const currentSettings = getState().settings.global;

      // Bereite die Daten für Firebase vor
      const dataToSave = {
        loyaltyCardActive: settings.loyaltyCardActive,
        loyaltyCardRidesRequired: settings.loyaltyCardRidesRequired,
        loyaltyCardDiscountEur: settings.loyaltyCardDiscountEur,
        loyaltyCardNoEndDate: settings.loyaltyCardNoEndDate,
        showPreordersToAllDrivers: settings.showPreordersToAllDrivers,
        autoAcceptPreBookings: settings.autoAcceptPreBookings,
        // Tarif-Einstellungen (nur speichern wenn vorhanden)
        ...(settings.tarifGrundpreis !== undefined && {
          tarifGrundpreis: settings.tarifGrundpreis,
          tarifPreisProKm: settings.tarifPreisProKm,
          tarifGrossraumzuschlag: settings.tarifGrossraumzuschlag,
        }),
        lastUpdated: serverTimestamp()
      };

      // Startdatum automatisch setzen wenn System aktiviert wird
      // (nur wenn es vorher inaktiv war oder noch kein Startdatum existiert)
      if (settings.loyaltyCardActive && !currentSettings.loyaltyCardActive) {
        // System wird gerade aktiviert -> neues Startdatum setzen
        dataToSave.loyaltyCardStartDate = serverTimestamp();
        console.log('Treuekarten-System wird aktiviert, setze Startdatum auf jetzt');
      } else if (settings.loyaltyCardActive && currentSettings.loyaltyCardStartDate) {
        // System bleibt aktiv -> Startdatum beibehalten
        dataToSave.loyaltyCardStartDate = Timestamp.fromDate(
          new Date(currentSettings.loyaltyCardStartDate)
        );
      } else if (!settings.loyaltyCardActive) {
        // System wird deaktiviert -> Startdatum bleibt erhalten (für Historie)
        if (currentSettings.loyaltyCardStartDate) {
          dataToSave.loyaltyCardStartDate = Timestamp.fromDate(
            new Date(currentSettings.loyaltyCardStartDate)
          );
        } else {
          dataToSave.loyaltyCardStartDate = null;
        }
      }

      // Enddatum nur speichern wenn nicht "unbestimmte Zeit"
      if (!settings.loyaltyCardNoEndDate && settings.loyaltyCardEndDate) {
        dataToSave.loyaltyCardEndDate = Timestamp.fromDate(
          new Date(settings.loyaltyCardEndDate)
        );
      } else {
        dataToSave.loyaltyCardEndDate = null;
      }

      // Prüfe ob Dokument existiert
      const settingsDoc = await getDoc(settingsRef);
      if (settingsDoc.exists()) {
        await updateDoc(settingsRef, dataToSave);
      } else {
        await setDoc(settingsRef, {
          ...dataToSave,
          createdAt: serverTimestamp()
        });
      }

      console.log('Einstellungen erfolgreich gespeichert:', dataToSave);

      return {
        ...settings,
        loyaltyCardStartDate: settings.loyaltyCardActive && !currentSettings.loyaltyCardActive
          ? new Date().toISOString().split('T')[0]
          : currentSettings.loyaltyCardStartDate,
        lastUpdated: new Date().toISOString()
      };
    } catch (error) {
      console.error('Fehler beim Speichern der Einstellungen:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Lädt die Cloud Function Einstellungen aus Firebase (appSettings/cloudfunctions)
 */
export const fetchCloudFunctionSettings = createAsyncThunk(
  'settings/fetchCloudFunctionSettings',
  async (_, { rejectWithValue }) => {
    try {
      const cfRef = doc(db, SETTINGS_COLLECTION, CF_SETTINGS_DOC_ID);
      const cfDoc = await getDoc(cfRef);
      if (cfDoc.exists()) {
        return { ...defaultCloudFunctionSettings, ...cfDoc.data() };
      }
      return defaultCloudFunctionSettings;
    } catch (error) {
      console.error('Fehler beim Laden der CF-Einstellungen:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Speichert eine einzelne Cloud Function Einstellung sofort (appSettings/cloudfunctions)
 */
export const saveCloudFunctionSetting = createAsyncThunk(
  'settings/saveCloudFunctionSetting',
  async ({ key, value }, { rejectWithValue }) => {
    try {
      const cfRef = doc(db, SETTINGS_COLLECTION, CF_SETTINGS_DOC_ID);
      await updateDoc(cfRef, { [key]: value });
      return { [key]: value };
    } catch (error) {
      console.error('Fehler beim Speichern der CF-Einstellung:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Startet einen Echtzeit-Listener für die Einstellungen
 */
export const startSettingsListener = () => (dispatch) => {
  console.log('Starte Einstellungen-Listener...');

  // Existierenden Listener entfernen
  if (settingsListener) {
    settingsListener();
    settingsListener = null;
  }

  const settingsRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);

  settingsListener = onSnapshot(
    settingsRef,
    (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        dispatch(updateSettingsRealtime({
          ...defaultSettings,
          ...data,
          loyaltyCardStartDate: data.loyaltyCardStartDate
            ? data.loyaltyCardStartDate.toDate().toISOString().split('T')[0]
            : null,
          loyaltyCardEndDate: data.loyaltyCardEndDate
            ? data.loyaltyCardEndDate.toDate().toISOString().split('T')[0]
            : null,
          lastUpdated: data.lastUpdated
            ? data.lastUpdated.toDate().toISOString()
            : null
        }));
      }
    },
    (error) => {
      console.error('Fehler im Einstellungen-Listener:', error);
    }
  );

  return () => {
    if (settingsListener) {
      settingsListener();
      settingsListener = null;
    }
  };
};

/**
 * Stoppt den Echtzeit-Listener
 */
export const stopSettingsListener = () => () => {
  console.log('Stoppe Einstellungen-Listener...');
  if (settingsListener) {
    settingsListener();
    settingsListener = null;
  }
};

// Initial State
const initialState = {
  // Globale Einstellungen (Firebase appSettings/general)
  global: {
    ...defaultSettings,
    lastUpdated: null
  },
  // Cloud Function Einstellungen (Firebase appSettings/cloudfunctions)
  cloudFunctions: {
    ...defaultCloudFunctionSettings,
  },
  // Lokale Einstellungen (localStorage)
  local: {
    darkMode: false,
    soundOnInstantOrder: true,
    soundOnPreorder: true
  },
  // Status
  isLoading: false,
  isSaving: false,
  error: null,
  isInitialized: false
};

// Slice
const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    // Lokale Einstellungen aktualisieren
    updateLocalSettings: (state, action) => {
      state.local = {
        ...state.local,
        ...action.payload
      };
      // In localStorage speichern
      try {
        localStorage.setItem('localSettings', JSON.stringify(state.local));
      } catch (error) {
        console.error('Fehler beim Speichern in localStorage:', error);
      }
    },
    // Lokale Einstellungen aus localStorage laden
    loadLocalSettings: (state) => {
      try {
        const saved = localStorage.getItem('localSettings');
        if (saved) {
          state.local = {
            ...state.local,
            ...JSON.parse(saved)
          };
        }
      } catch (error) {
        console.error('Fehler beim Laden aus localStorage:', error);
      }
    },
    // Echtzeit-Update der globalen Einstellungen
    updateSettingsRealtime: (state, action) => {
      state.global = {
        ...state.global,
        ...action.payload
      };
      state.isInitialized = true;
    },
    // Fehler zurücksetzen
    clearSettingsError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Settings
      .addCase(fetchSettings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSettings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.global = {
          ...state.global,
          ...action.payload
        };
        state.isInitialized = true;
      })
      .addCase(fetchSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Save Settings
      .addCase(saveSettings.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(saveSettings.fulfilled, (state, action) => {
        state.isSaving = false;
        state.global = {
          ...state.global,
          ...action.payload
        };
      })
      .addCase(saveSettings.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      })
      // Save Setting Immediately - Optimistisches Update
      .addCase(saveSettingImmediately.pending, (state, action) => {
        const { key, value } = action.meta.arg;
        state.global[key] = value;
      })
      .addCase(saveSettingImmediately.fulfilled, (state, action) => {
        state.global = {
          ...state.global,
          ...action.payload
        };
      })
      .addCase(saveSettingImmediately.rejected, (state, action) => {
        // Optimistisches Update rückgängig machen
        const { key, value } = action.meta.arg;
        state.global[key] = !value;
        state.error = action.payload;
      })
      // Fetch Cloud Function Settings
      .addCase(fetchCloudFunctionSettings.fulfilled, (state, action) => {
        state.cloudFunctions = { ...state.cloudFunctions, ...action.payload };
      })
      // Save Cloud Function Setting - Optimistisches Update
      .addCase(saveCloudFunctionSetting.pending, (state, action) => {
        const { key, value } = action.meta.arg;
        state.cloudFunctions[key] = value;
      })
      .addCase(saveCloudFunctionSetting.fulfilled, (state, action) => {
        state.cloudFunctions = { ...state.cloudFunctions, ...action.payload };
      })
      .addCase(saveCloudFunctionSetting.rejected, (state, action) => {
        state.error = action.payload;
      });
  }
});

export const {
  updateLocalSettings,
  loadLocalSettings,
  updateSettingsRealtime,
  clearSettingsError
} = settingsSlice.actions;

export default settingsSlice.reducer;
